<template>
    <div>
        <el-card v-if="isLoading" class="border rounded p-3 d-flex align-items-center">
            <i class="el-icon-loading mr-2"></i>
            Carregando distribuidor...
        </el-card>
        
        <el-card v-else-if="!isLoading && distributor">
            <el-page-header class="align-items-start" @back="goBack">
                <div slot="content" class="d-flex flex-wrap align-items-start justify-content-between mb-3">
                    <div class="d-flex flex-column">
                        <h6>{{ distributor.nome }}</h6>
                    </div>

                    <div class="d-flex">
                        <el-button 
                            type="success" 
                            size="mini" 
                            plain
                            :disabled="isLoading"
                            @click="showLoginAs()"
                        >
                            <i v-if="isLoading" class="el-icon-loading"></i>
                            <i v-else class="fas fa-sign-in-alt"></i>
                            Logar como
                        </el-button>
                        <el-button 
                            v-if="isAuthorized"
                            type="warning"
                            size="mini"
                            plain
                            :disabled="isLoading"
                            @click="dispatchChangePassword(scope.row.id)"
                        >
                            <i v-if="isLoading" class="el-icon-loading"></i>
                            <i v-else class="fas fa-key"></i>
                            Alterar senha
                        </el-button>
                    </div>
                </div>
            </el-page-header>

            <el-row :gutter="12">
                <el-col :span="12">
                    <el-descriptions title="Informações" :column="6" size="mini" :border="true" class="my-3">
                        <!-- Nome -->
                        <el-descriptions-item :span="6">
                            <template slot="label">Distribuidor</template>
                            <div class="d-flex align-items-center justify-content-between">
                                <span>{{ distributor.nome }}</span>
                                <div class="d-flex align-items-center">
                                    <el-tag class="mr-2" type="info" size="mini">#{{ distributor.codigo }}</el-tag>
                                    <el-button size="mini" circle @click="copyThis(distributor.codigo)">
                                        <i class="el-icon-document-copy"></i>
                                    </el-button>
                                </div>
                            </div>
                        </el-descriptions-item>

                        <!-- Classe -->
                        <el-descriptions-item :span="2">
                            <template slot="label">Classe</template>
                            <el-tag size="mini">{{ distributor.classe || '-' }}</el-tag>
                        </el-descriptions-item>

                        <!-- Status -->
                        <el-descriptions-item :span="2">
                            <template slot="label">Status</template>
                            <el-tag 
                                v-if="distributor.status" 
                                :type="distributor.status.nome === 'Ativo' 
                                    ? 'success' 
                                    : (distributor.status.nome == 'Inativo' ? 'warning' : 'danger')"
                                size="mini"
                            >
                                {{ distributor.status.nome }}
                            </el-tag>
                        </el-descriptions-item>

                        <!-- Criado em --> 
                        <el-descriptions-item 
                            v-if="distributor.created_at.date" 
                            :span="2"
                        >
                            <template slot="label">Criado em</template>
                            {{ distributor.created_at.date }}
                        </el-descriptions-item>

                        <!-- CPF -->
                        <el-descriptions-item 
                            v-if="distributor && distributor.pessoa_fisica" 
                            :span="6"
                        >
                            <template slot="label">CPF</template>

                            <div class="d-flex align-items-center justify-content-between">
                                <div>
                                    <el-skeleton v-if="isLoading"/>
                                    <span v-else>{{ distributor.pessoa_fisica.cpf || '-' }}</span>
                                </div>
                                <el-button v-if="distributor.cpf" size="mini" circle @click="copyThis(distributor.pessoa_fisica.cpf)">
                                    <i class="el-icon-document-copy"></i>
                                </el-button>
                            </div>
                        </el-descriptions-item>

                        <!-- CNPJ -->
                        <el-descriptions-item 
                            v-else-if="distributor && distributor.pessoa_juridica" 
                            :span="6"
                        >
                            <template slot="label">CNPJ</template>
                            <div class="d-flex align-items-center justify-content-between">
                                {{ distributor.pessoa_juridica.cnpj || '-' }}
                                <el-button v-if="distributor.pessoa_juridica.cnpj" size="mini" circle @click="copyThis(distributor.pessoa_juridica.cnpj)">
                                    <i class="el-icon-document-copy"></i>
                                </el-button>
                            </div>
                        </el-descriptions-item>
                    </el-descriptions>
                </el-col>

                <el-col :span="12">
                    <el-descriptions title="Contatos" :column="4" size="mini" :border="true" class="my-3">
                        <!-- Email -->
                        <el-descriptions-item :span="4">
                            <template slot="label">
                                <i clsas="fas fa-envelope"></i>
                                E-mail
                            </template>
                            <div class="d-flex align-items-center justify-content-between">
                                <el-tag type="info" size="mini">{{ distributor.email || '-' }}</el-tag>
                                <el-button v-if="distributor.email" size="mini" circle @click="copyThis(distributor.email)">
                                    <i class="el-icon-document-copy"></i>
                                </el-button>
                            </div>
                        </el-descriptions-item>

                        <!-- Contato -->
                        <template v-if="distributor && distributor.contatos.length > 0">
                            <el-descriptions-item 
                                v-for="(contact, index) in distributor.contatos" 
                                :key="contact.id"
                                :span="2"
                            >
                            <template slot="label">
                                {{ contact.telefone.tipo == 'F' ? 'Fixo' : 'Celular' }}
                            </template>
                            <div class="d-flex align-items-center justify-content-between">
                                <el-tag size="mini">({{ contact.telefone.ddd }}) {{ contact.telefone.numero }}</el-tag>
                                <el-button size="mini" circle @click="copyThis(`(${contact.ddd})$ {contact.numero}`)">
                                    <i class="el-icon-document-copy"></i>
                                </el-button>
                            </div>
                            </el-descriptions-item>
                        </template>

                        <!-- Endereco -->
                        <el-descriptions-item 
                            v-if="distributor && distributor.endereco.endereco_formatado" 
                            :span="4"
                        >
                            <template slot="label">Endereço</template>
                            {{ distributor.endereco.endereco_formatado }}
                        </el-descriptions-item>
                    </el-descriptions>
                </el-col>
            </el-row>

            <!-- content -->
            <el-tabs type="border-card" v-model="activeName">
                <el-tab-pane name="first">
                    <span slot="label">
                        <i class="el-icon-folder"></i> 
                        <span class="ml-1">Anexos</span>
                    </span>

                    <div>
                        <el-tag class="mb-3" type="warning">
                            <i class="el-icon-warning-outline"></i> 
                            Este distribuidor já foi aprovado, atenção ao modificar anexos já existentes, o ideal é apenas a adição novos de arquivos.
                        </el-tag>
                        <Anexos v-if="getProspect != null" :prospect="getProspect"/>
                        <el-empty v-else description="Não existe um prospecto associado a este distribuidor"></el-empty>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-card>

        <el-card v-else>
            <el-empty description="Distribuidor não encontrado"></el-empty>
        </el-card>

        <!-- ChangePassword -->
        <DistribuidorChangePassword ref="changePassword"/>
    </div>
</template>
<script>
    // Components
    import { 
        Row,
        Col,
        Card, 
        PageHeader, 
        Tag, 
        Notification, 
        Tabs, 
        TabPane, 
        Loading, 
        Message, 
        Empty,
        Descriptions,
        DescriptionsItem,
        Button,
    } from 'element-ui';

    import Anexos from "../adm_prospecto/partials/Anexos.vue";
    import DistribuidorChangePassword from "@/components/_internal/distribuidor/DistribuidorChangePassword.vue";

    // Utils
    import { copyThis } from "@/util/copyThis.js";
    
    // Api
    import { DistribuidorApi } from "@/api/DistribuidorApi.js";
    const distribuidorApi = new DistribuidorApi();
    
    export default {
        props: {
            id: {
                type: [String, Number],
                default: null,
                required: true
            }
        },
        data() {
            return {
                distributor: null,
                activeName: 'first',
                isLoading: false,
            }
        },
        computed: {
            isAuthorized() {
                return this.$store.getters['user/isAnyRole'](['adm']);
            },  
            getProspect() {
                if (this.distributor.prospecto == null) {
                    return null
                }

                return this.distributor.prospecto
            }
        },
        methods: {
            async fetch() {
                try {
                    this.isLoading = true

                    const res = await distribuidorApi.show(this.id)

                    this.distributor = res
                } catch (error) {
                    if (error.response.status != 404) {
                        Notification.error({
                            title: 'Erro',
                            message: 'Não foi possível obter o distribuidor. Algo inesperado ocorreu!'
                        })
                    }

                    Notification.info({
                        title: 'Info',
                        message: error.response.message ?? 'Distribuidor não encontrado',
                    })

                } finally {
                    this.isLoading = false
                }
            },
            /** ChangePassword */
            async dispatchChangePassword(id) {
                this.$refs.changePassword.id = id;

                const elLoading = this.$loading({ lock: true })

                await this.$refs.changePassword.show();

                elLoading.close();

                return this.$refs.changePassword.toggleModal();
            },

            showLoginAs() {
                this.$confirm('Deseja realmente logar com este distribuidor?', 'Logar como distribuidor', {
                    confirmButtonText: 'Sim',
                    cancelButtonText: 'Cancelar',
                }).then(async () => {
                    this.isLoading = true

                    window.location.assign(`/adm/logarComo/${this.distributor.user_id}`);
                }).finally(() => {
                    this.isLoading = false
                })
            },
            goBack() {
                window.location.assign('/adm/distribuidores')
            },
            copyThis
        },
        mounted() {
            this.fetch()
        },
        components: {
            [Row.name]: Row,
            [Col.name]: Col,
            [Card.name]: Card,
            [PageHeader.name]: PageHeader,
            [Tag.name]: Tag,
            [Notification.name]: Notification,
            [Tabs.name]: Tabs,
            [TabPane.name]: TabPane, 
            [Loading.name]: Loading, 
            [Message.name]: Message,
            [Button.name]: Button,
            [Empty.name]: Empty,
            [Descriptions.name]: Descriptions,
            [DescriptionsItem.name]: DescriptionsItem,
            Anexos,
            DistribuidorChangePassword,
        }
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/distribuidor/Show.vue